import React, { useMemo } from 'react';

import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Offer } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import { EPopupSubType, SortingOffersValue } from 'constants/enums';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getConvertedPrice } from 'utils/getConvertedPrice';

import ActionCell from 'components/DataTableComponent/components/ActionCell';
import SortableHeader from 'components/SortableHeader/SortableHeader';

import { IOfferColumnsBaseProps } from '../../../Offer.types';
import { renderOfferStatusChip } from '../../../Offer.utils';

dayjs.extend(utc);

interface IUsePopupsColumnsProps extends IOfferColumnsBaseProps {
  isPricingsLoading: boolean;
  pricingsResult: any;
  isTriggeredPopups: boolean;
}

interface IUsePopupsColumnsReturn {
  columns: ColumnDef<Offer>[];
}

/**
 * Hook that builds the column definitions for the Popups table.
 *
 * @example
 * const { columns } = usePopupsColumns({
 *   sorting, updateSorting, isLoading, actions, permissions,
 *   isPricingsLoading, pricingsResult
 * });
 */
export const usePopupsColumns = ({
  sorting,
  updateSorting,
  isLoading,
  actions,
  permissions,
  isPricingsLoading,
  pricingsResult,
  isTriggeredPopups
}: IUsePopupsColumnsProps): IUsePopupsColumnsReturn => {
  const idColumn: ColumnDef<Offer> = {
    accessorKey: 'offerId',
    header: () => (
      <SortableHeader
        label="ID"
        sortValue={SortingOffersValue.ID}
        currentSortValue={sorting.sortValue}
        currentDirection={sorting.direction}
        onSort={updateSorting}
      />
    ),
    cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
  };

  const externalIdColumn: ColumnDef<Offer> = {
    accessorKey: 'publisherOfferId',
    header: () => (
      <SortableHeader
        label="External ID"
        sortValue={SortingOffersValue.PUBLISHER_OFFER_ID}
        currentSortValue={sorting.sortValue}
        currentDirection={sorting.direction}
        onSort={updateSorting}
      />
    ),
    cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
  };

  const nameColumn: ColumnDef<Offer> = {
    accessorKey: 'name',
    header: () => (
      <SortableHeader
        label="Name"
        sortValue={SortingOffersValue.NAME}
        currentSortValue={sorting.sortValue}
        currentDirection={sorting.direction}
        onSort={updateSorting}
      />
    ),
    cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
  };

  const typeColumn: ColumnDef<Offer> = {
    accessorKey: 'subType',
    header: 'Type',
    cell: (cell: CellContext<Offer, unknown>) => cell.getValue()
  };

  const offerDesignColumn: ColumnDef<Offer> = {
    accessorKey: 'offerDesign',
    header: 'Offer Design',
    cell: ({ row }: CellContext<Offer, unknown>) => {
      const offerUi = row.original.offerUi;
      return offerUi?.name || offerUi?.offerUiId || '-';
    }
  };

  const scheduleColumn: ColumnDef<Offer> = {
    accessorKey: 'schedule',
    header: 'Schedule',
    cell: ({ row }: CellContext<Offer, unknown>) => {
      if (row.original.subType === EPopupSubType.DAILY_BONUS) {
        const filteredProducts =
          (row.original.productsSequence as Array<{ products: unknown[] }>)?.filter(
            (item) => item.products.length > 0
          ) || [];
        return filteredProducts.length > 0
          ? `${filteredProducts.length} days`
          : '-';
      }
      return '-';
    }
  };

  const priceColumn: ColumnDef<Offer> = {
    accessorKey: 'price',
    header: () => (
      <SortableHeader
        label="Price"
        sortValue={SortingOffersValue.PRICE}
        currentSortValue={sorting.sortValue}
        currentDirection={sorting.direction}
        onSort={updateSorting}
      />
    ),
    cell: ({ row }: CellContext<Offer, unknown>) => {
      if (row.original.subType === EPopupSubType.DAILY_BONUS) {
        return '-';
      }
      if (isLoading || isPricingsLoading) {
        return '';
      }
      return getConvertedPrice(row.original, pricingsResult);
    }
  };

  const createdAtColumn: ColumnDef<Offer> = {
    accessorKey: 'createdAt',
    header: () => (
      <SortableHeader
        label="Created At"
        sortValue={SortingOffersValue.CREATED_AT}
        currentSortValue={sorting.sortValue}
        currentDirection={sorting.direction}
        onSort={updateSorting}
      />
    ),
    cell: ({ row }: CellContext<Offer, unknown>) => {
      if (isLoading) {
        return '';
      }
      return (
        <span className="whitespace-nowrap">
          {dayjs(row.original.createdAt).utc().format(DATE_TIME_FORMAT)}
        </span>
      );
    }
  };

  const triggersColumn: ColumnDef<Offer> = {
    accessorKey: 'triggers',
    header: 'Triggers',
    cell: ({ row }: CellContext<Offer, unknown>) => {
      const triggers = row.original.triggers;
      if (!triggers || triggers.length === 0) {
        return '-';
      }
      const trigger = triggers[0];
      const eventName = trigger.eventName || trigger.type || '-';
      return <span className="truncate max-w-[100px] block">{eventName}</span>;
    }
  };

  const statusColumn: ColumnDef<Offer> = {
    accessorKey: 'active',
    header: () => (
      <SortableHeader
        label="Status"
        sortValue={SortingOffersValue.STATUS}
        currentSortValue={sorting.sortValue}
        currentDirection={sorting.direction}
        onSort={updateSorting}
      />
    ),
    accessorFn: (row: Offer) => {
      return row.active ? 'Active' : 'Inactive';
    },
    cell: ({ row }: CellContext<Offer, unknown>) => {
      return renderOfferStatusChip(row.original);
    }
  };

  const actionsColumn: ColumnDef<Offer> = {
    accessorKey: 'actions',
    header: '',
    maxSize: 60,
    minSize: 60,
    size: 60,
    cell: ({ row }: CellContext<Offer, unknown>) => {
      const { offerId } = row.original;
      if (!offerId) {
        return null;
      }
      return (
        <div className="max-w-[60px] w-[60px]">
          <ActionCell
            rowData={row.original}
            actions={[
              {
                label: 'Edit',
                handler: () => {
                  actions.onEdit(offerId);
                },
                disabled:
                  !permissions.canUserEdit || !permissions.canManageOffers,
                hidden: !permissions.canManageOffers
              },
              {
                label: 'Edit',
                handler: () => {
                  actions.onLimitedEdit(offerId);
                },
                disabled:
                  !permissions.canUserEdit || !permissions.canUpdateStatusDesign,
                hidden:
                  !permissions.canUpdateStatusDesign ||
                  permissions.canManageOffers
              },
              {
                label: row.original.active ? 'Deactivate' : 'Activate',
                handler: () =>
                  actions.onToggleStatus(offerId, row.original.active),
                disabled: !permissions.canUserEdit
              },
              {
                label: 'Duplicate',
                handler: () => actions.onDuplicate(offerId),
                disabled: !permissions.canUserEdit || !permissions.canManageOffers
              },
              {
                label: 'Delete',
                handler: () => actions.onDelete(offerId),
                disabled: !permissions.canUserEdit || !permissions.canManageOffers
              }
            ]}
          />
        </div>
      );
    }
  };

  const columns = useMemo((): ColumnDef<Offer>[] => {
    let offerOrTypeColumn = offerDesignColumn;

    let additionalInformationColumns = [
      createdAtColumn,
      triggersColumn,
      statusColumn
    ];

    if (isTriggeredPopups) {
      offerOrTypeColumn = typeColumn;
      additionalInformationColumns = [statusColumn, createdAtColumn];
    }

    return [
      idColumn,
      externalIdColumn,
      nameColumn,
      offerOrTypeColumn,
      scheduleColumn,
      priceColumn,
      ...additionalInformationColumns,
      actionsColumn
    ];
  }, [
    sorting,
    updateSorting,
    isLoading,
    isPricingsLoading,
    pricingsResult,
    actions,
    permissions,
    isTriggeredPopups
  ]);

  return { columns };
};
